<script setup lang="ts">
const props = defineProps<{
  defaultSidebarWidth?: number;
  minSidebarWidth?: number;
  maxSidebarWidth?: number;
}>();

const defaultWidth = props.defaultSidebarWidth ?? 280;
const minWidth = props.minSidebarWidth ?? 200;
const maxWidth = props.maxSidebarWidth ?? 400;

const sidebarWidth = ref(defaultWidth);
const isCollapsed = ref(false);
const isResizing = ref(false);

// Persist sidebar state
onMounted(() => {
  try {
    const storedWidth = localStorage.getItem("book-sidebar-width");
    const storedCollapsed = localStorage.getItem("book-sidebar-collapsed");

    if (storedWidth) sidebarWidth.value = parseInt(storedWidth, 10);
    if (storedCollapsed) isCollapsed.value = storedCollapsed === "true";
  } catch {
    // Ignore localStorage errors
  }
});

watch(sidebarWidth, (width) => {
  try {
    localStorage.setItem("book-sidebar-width", String(width));
  } catch {
    // Ignore
  }
});

watch(isCollapsed, (collapsed) => {
  try {
    localStorage.setItem("book-sidebar-collapsed", String(collapsed));
  } catch {
    // Ignore
  }
});

function toggleSidebar() {
  isCollapsed.value = !isCollapsed.value;
}

function startResize(e: MouseEvent) {
  if (isCollapsed.value) return;

  isResizing.value = true;
  const startX = e.clientX;
  const startWidth = sidebarWidth.value;

  function onMouseMove(e: MouseEvent) {
    const delta = e.clientX - startX;
    const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidth + delta));
    sidebarWidth.value = newWidth;
  }

  function onMouseUp() {
    isResizing.value = false;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }

  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);
}
</script>

<template>
  <div class="flex h-screen overflow-hidden bg-white dark:bg-gray-900">
    <!-- Sidebar -->
    <aside
      class="relative flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 transition-all duration-200"
      :style="{ width: isCollapsed ? '0px' : `${sidebarWidth}px` }"
    >
      <div class="h-full overflow-hidden" :class="{ invisible: isCollapsed }">
        <slot name="sidebar" />
      </div>

      <!-- Resize handle -->
      <div
        v-if="!isCollapsed"
        class="absolute top-0 right-0 bottom-0 w-1 cursor-col-resize hover:bg-primary-500/50 transition-colors"
        :class="{ 'bg-primary-500': isResizing }"
        @mousedown="startResize"
      />
    </aside>

    <!-- Toggle button -->
    <button
      type="button"
      class="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-r-md transition-colors"
      :style="{ left: isCollapsed ? '0px' : `${sidebarWidth}px` }"
      @click="toggleSidebar"
    >
      <UIcon
        :name="
          isCollapsed ? 'i-heroicons-chevron-right' : 'i-heroicons-chevron-left'
        "
        class="w-4 h-4 text-gray-600 dark:text-gray-300"
      />
    </button>

    <!-- Main content -->
    <main class="flex-1 overflow-hidden">
      <slot name="content" />
    </main>
  </div>
</template>
